import React, { useRef, useMemo, useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "components/custom/toastify-custom.css";
import "assets/scss/forms/validation.scss"; // Import custom validation styles
import VariantFormCard from "components/cards/VariantFormCard";
import ProductVariantsTable from "components/custom/ProductVariantsTable";
import ProductTabs from "components/tabs/ProductTabs";
import productAttributes from "config/productAttributes";
import { useTranslation } from "react-i18next";
import ProductHeader from "components/products/forms/ProductHeader";
import ProductAttributeGroup from "components/products/forms/ProductAttributeGroup";
import ProductVideoField from "components/products/forms/ProductVideoField";
import useProductForm from "hooks/products/form/useProductForm";
import useProductPublish from "hooks/products/form/useProductPublish";
import { AttributeGroup } from "components/products/types";
import { calculateCommission, canCalculateCommission, CommissionCalculationResult } from "utils/commissionCalculator";
import CommissionCalculationModal from "components/modals/CommissionCalculationModal";

/**
 * Product Add Page
 * 
 * This component allows users to create products with various attributes,
 * images, and variants. It has been refactored to follow React best practices:
 * 
 * 1. Custom hooks for form state and business logic
 * 2. Reusable components for UI elements
 * 3. Clear separation of concerns (presentation vs logic)
 * 4. Proper memoization to avoid unnecessary re-renders
 */
const AddProduct: React.FC = () => {
  const tableHeaderRef = useRef<HTMLTableSectionElement | null>(null);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  
  // Commission calculation state
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [commissionResult, setCommissionResult] = useState<CommissionCalculationResult | null>(null);
  
  // Add event listener for tab switching
  useEffect(() => {
    const handleTabSwitch = (event: Event) => {
      const customEvent = event as CustomEvent<{ tabName: string }>;
      if (customEvent.detail && customEvent.detail.tabName) {
        setActiveTabKey(customEvent.detail.tabName);
      }
    };
    
    // Add event listener
    document.addEventListener('switch-to-tab', handleTabSwitch);
    
    // Clean up
    return () => {
      document.removeEventListener('switch-to-tab', handleTabSwitch);
    };
  }, []);

  // Get attribute groups from configuration
  const commonAttributeGroups = useMemo(() => 
    productAttributes.CommonAttributes.AttributeGroups.filter(
      (group) => !group.display || group.display !== "inTab"
    ), []);

  const inTabAttributeGroups = useMemo(() => 
    productAttributes.CommonAttributes.AttributeGroups.filter(
      (group) => group.display && group.display === "inTab"
    ), []);

  // Initialize form state and handlers
  const {
    // State
    formValues,
    loading: formLoading,
    characteristicsAttributes,
    variationAttributes,
    selectedAttributes,
    initialCategorySelection,
    dynamicOptions,
    brandOptions,
    generatedVariants,
    isLoadingOptions,
    isLoadingAttributes,
    invalidFields: formInvalidFields,  // Renamed to formInvalidFields
    variantCreationError,
    
    // Handlers
    handleChange,
    handleInputChange,
    fetchCategories,
    handleCategorySelection,
    handleClear,
    handleSaveDraft,
    handleCreateVariants,
    handleRemoveVariant,
    handleDeleteVariant,
    handleUpdateVariantImage,
    handleUpdateVariant,
    handleFileDrop,
    handleFileRemove,
    handleFileUpdate,
    handleFileIndexChange,
  } = useProductForm({ 
    commonAttributeGroups,
    allTabGroups: inTabAttributeGroups
  });

  // Get tab groups with characteristics attributes
  const allTabGroups = useMemo(() => 
    getAllTabGroups(inTabAttributeGroups, characteristicsAttributes), 
    [inTabAttributeGroups, characteristicsAttributes]
  );

  // Initialize publishing functionality
  const {
    loading: publishLoading,
    invalidFields,
    handlePublishProduct
  } = useProductPublish({
    formValues,
    characteristicsAttributes,
    selectedAttributes,
    generatedVariants,
    commonAttributeGroups,
    allTabGroups,
    onClear: handleClear
  });

  // Combine the loading states and invalidFields
  const isLoading = formLoading || publishLoading;
  
  // Combine invalidFields from both hooks to ensure all validation errors are shown
  const combinedInvalidFields = [...formInvalidFields, ...invalidFields];

  // Commission calculation handlers
  const handleCalculateCommission = () => {
    if (!canCalculateCommission(formValues)) {
      // Show error message using toast
      toast.error(t('products.commission.missingFields', 'Please fill in all required fields: Price, Weight, Dimensions (Length, Width, Height), and Shipping Method'));
      return;
    }

    try {
      // Extract the shipping method ID from the form values
      // Must match the enum values in CommissionDetailsModal.tsx (FBW = 16500, ESS = 16501, ESW = 18383)
      let shippingMethodId = 0;
      if (formValues.mode_expedition) {
        // Handle different types of mode_expedition value
        if (typeof formValues.mode_expedition === 'number') {
          // If it's a number, use it directly
          shippingMethodId = formValues.mode_expedition;
        } else if (typeof formValues.mode_expedition === 'string') {
          // If it's a string that can be converted to a number, use that
          if (!isNaN(Number(formValues.mode_expedition))) {
            shippingMethodId = Number(formValues.mode_expedition);
          }
        } else if (typeof formValues.mode_expedition === 'object') {
          // If it's an object, check if it's an array or a single object
          if (Array.isArray(formValues.mode_expedition)) {
            // If it's an array and has items, use the option_id of the first item if available
            if (formValues.mode_expedition.length > 0 && 
                typeof formValues.mode_expedition[0] === 'object' && 
                'option_id' in formValues.mode_expedition[0]) {
              shippingMethodId = Number(formValues.mode_expedition[0].option_id);
            }
          } else if (formValues.mode_expedition !== null && 'option_id' in formValues.mode_expedition) {
            // If it's a single object with option_id, use that
            shippingMethodId = Number(formValues.mode_expedition.option_id);
          }
        }
      }
      
      console.log('Shipping method for commission calculation:', {
        rawValue: formValues.mode_expedition,
        extractedId: shippingMethodId
      });
      
      const calculationInput = {
        price: Number(formValues.price || 0),
        specialPrice: formValues.special_price ? Number(formValues.special_price) : null,
        discountStartDate: formValues.special_from_date || null,
        discountEndDate: formValues.special_to_date || null,
        weight: Number(formValues.weight || 0),
        length: Number(formValues.longueur25 || 0),
        width: Number(formValues.dimensions_de_l_outil_largeur1 || 0),
        height: Number(formValues.dimensions_de_l_outil_hauteur1 || 0),
        shippingMethod: shippingMethodId,
      };

      const result = calculateCommission(calculationInput);
      setCommissionResult(result);
      setShowCommissionModal(true);
    } catch (error) {
      console.error('Error calculating commission:', error);
      toast.error(t('products.commission.calculationError', 'Error calculating commission. Please check your inputs.'));
    }
  };

  const handleCloseCommissionModal = () => {
    setShowCommissionModal(false);
  };

  // Check if commission can be calculated
  const commissionCanBeCalculated = canCalculateCommission(formValues);

  return (
    <div>
      {/* Toast container for notifications */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        className="toastify-container"
        toastClassName="toastify-toast"
      />

      <form className="mb-12">
        {/* Header with action buttons */}
        <ProductHeader
          loading={isLoading}
          onClear={handleClear}
          onSaveDraft={handleSaveDraft}
          onPublish={handlePublishProduct}
          onCalculateCommission={handleCalculateCommission}
          canCalculateCommission={commissionCanBeCalculated}
        />

        <Row className="g-5">
          {/* Left side: common attributes + tabs */}
          <Col xs={12} xl={8}>
            {/* Render common attribute groups */}
            {commonAttributeGroups.map((group, index) => (
              <ProductAttributeGroup
                key={`group-${index}`}
                group={group}
                formValues={formValues}
                invalidFields={combinedInvalidFields}
                isLoadingOptions={isLoadingOptions}
                dynamicOptions={dynamicOptions}
                brandOptions={brandOptions}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onFileDrop={handleFileDrop}
                onFileRemove={handleFileRemove}
                onFileUpdate={handleFileUpdate}
                onFileIndexChange={handleFileIndexChange}
                onCategorySelected={handleCategorySelection}
                fetchCategories={fetchCategories}
                initialCategorySelection={initialCategorySelection}
              />
            ))}

            {/* Product Video Field */}
            <ProductVideoField
              formValues={formValues}
              onChange={handleChange}
              invalidFields={combinedInvalidFields}
            />

            {/* Product tabs with tabbed attribute groups */}
            <ProductTabs
              AttributeGroups={allTabGroups}
              formValues={formValues}
              handleChange={handleChange}
              invalidFields={combinedInvalidFields}
              setToastMessage={(message: string) => {}}  // Not needed with react-toastify
              setToastVariant={(variant: "success" | "danger") => {}}  // Not needed with react-toastify
              setShowToast={(show: boolean) => {}}  // Not needed with react-toastify
              activeKey={activeTabKey || undefined}
              onSelect={(key) => setActiveTabKey(key)}
            />
          </Col>

          {/* Right side: Variation form card */}
          <Col xs={12} xl={4}>
            <Row className="g-2">
              <Col xs={12} xl={12}>
              <VariantFormCard
                variationAttributes={variationAttributes}
                handleChange={handleChange}
                formValues={formValues}
                onCreateVariants={handleCreateVariants}
                onRemoveVariant={handleRemoveVariant}
                isLoadingAttributes={isLoadingAttributes}
                validationError={variantCreationError}
                key={`variant-form-${selectedAttributes.length}-${generatedVariants.length}`}
              />
              </Col>
            </Row>
          </Col>
        </Row>
      </form>

      {/* Table of generated variants */}
      <Row className="g-2">
        <Col xs={12} xl={12}>
          {generatedVariants.length > 0 && (
            <ProductVariantsTable
              tableHeaderRef={tableHeaderRef}
              variantsData={generatedVariants}
              selectedAttributes={selectedAttributes}
              onDeleteVariant={handleDeleteVariant}
              onUpdateVariant={handleUpdateVariant}
              formValues={formValues}
              onUpdateVariantImage={handleUpdateVariantImage}
            />
          )}
        </Col>
      </Row>

      {/* Commission Calculation Modal */}
      <CommissionCalculationModal
        show={showCommissionModal}
        onHide={handleCloseCommissionModal}
        calculationResult={commissionResult}
      />
    </div>
  );
};

/**
 * Helper function to generate all tab groups including the dynamic characteristics
 */
function getAllTabGroups(inTabAttributeGroups: AttributeGroup[], characteristicsAttributes: any[]): AttributeGroup[] {
  const allTabGroups = [...inTabAttributeGroups];
  
  const characteristicsGroup = allTabGroups.find(
    (group) => group.name === "Caractéristiques"
  );

  if (characteristicsGroup) {
    if (Array.isArray(characteristicsGroup.attributes)) {
      characteristicsGroup.attributes = characteristicsAttributes;
    }
  } else {
    allTabGroups.push({
      name: "Caractéristiques",
      icon: "sliders",
      attributes: characteristicsAttributes,
    });
  }
  
  return allTabGroups;
}

export default AddProduct;
